import {
  ContractReportFilterOption,
  IContractSigningSource,
  IContractStatusHistoryItem,
} from './types'

export type SigningState =
  | 'not_sent'
  | 'not_required_signing'
  | 'pending_signature'
  | 'signed'

const COMBINED_NOT_SENT_LABEL = 'Не отправлен/не требует подписания'

export const DEFAULT_SIGNING_STATE_OPTIONS: ContractReportFilterOption[] = [
  {
    value: 'not_sent',
    label: 'Не отправлен',
  },
  {
    value: 'not_required_signing',
    label: 'Не требует подписания',
  },
  {
    value: 'pending_signature',
    label: 'На подписании',
  },
  {
    value: 'signed',
    label: 'Подписан',
  },
]

export const normalizeSigningStateFilterOptions = (
  options?: ContractReportFilterOption[] | null,
): ContractReportFilterOption[] => {
  if (!options?.length) {
    return DEFAULT_SIGNING_STATE_OPTIONS
  }

  const hasSplitStatuses = options.some(
    (option) => option.value === 'not_required_signing',
  )

  if (hasSplitStatuses) {
    return options.map((option) => {
      if (option.value === 'not_sent' && option.label?.includes('/')) {
        return { ...option, label: 'Не отправлен' }
      }

      return option
    })
  }

  const hasCombinedNotSent = options.some(
    (option) =>
      option.value === 'not_sent' &&
      (option.label === COMBINED_NOT_SENT_LABEL || option.label?.includes('/')),
  )

  if (hasCombinedNotSent) {
    return DEFAULT_SIGNING_STATE_OPTIONS
  }

  return options
}

export const mapSigningStateToContractStatus = (
  signingState: string,
): string => {
  switch (signingState) {
    case 'signed':
      return 'signed'
    case 'pending_signature':
      return 'sent'
    case 'not_required_signing':
      return 'not_required_signing'
    case 'not_sent':
    default:
      return 'new'
  }
}

export const isContractNotRequiredSigning = (
  signingState?: string | null,
): boolean => signingState === 'not_required_signing'

export const isContractPendingSignature = (
  signingState?: string | null,
): boolean => signingState === 'pending_signature'

export const getSigningStateColor = (
  signingState?: string | null,
  backendColor?: string | null,
): string => {
  if (backendColor) {
    return backendColor
  }

  switch (signingState) {
    case 'signed':
      return 'var(--color-status-success)'
    case 'pending_signature':
      return 'var(--color-status-warning)'
    case 'not_required_signing':
      return 'var(--color-status-neutral-text)'
    case 'not_sent':
    default:
      return 'var(--color-status-neutral-text)'
  }
}

export type ContractStatusHistoryViewItem = {
  id?: number
  dateLabel: string
  statusLabel: string
}

const parseHistoryDateLabel = (
  label: string,
): { dateLabel: string; timeLabel?: string } => {
  const isoMatch = label.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}:\d{2}))?/)
  if (isoMatch) {
    const [, year, month, day, time] = isoMatch
    return {
      dateLabel: `${day}.${month}.${year}`,
      timeLabel: time,
    }
  }

  const ruMatch = label.match(/^(\d{2}\.\d{2}\.\d{4})(?:\s+(\d{2}:\d{2}))?/)
  if (ruMatch) {
    const [, date, time] = ruMatch
    return {
      dateLabel: date,
      timeLabel: time,
    }
  }

  return { dateLabel: label }
}

export const mapContractStatusHistory = (
  history?: IContractStatusHistoryItem[] | null,
): ContractStatusHistoryViewItem[] => {
  if (!history?.length) {
    return []
  }

  return history.map((entry) => {
    const rawDate = entry.created_at_label ?? String(entry.created_at ?? '—')
    const { dateLabel, timeLabel } = parseHistoryDateLabel(String(rawDate))

    return {
      id: entry.id,
      dateLabel: timeLabel ? `${dateLabel} ${timeLabel}` : dateLabel,
      statusLabel: entry.signing_state_label ?? '—',
    }
  })
}

const formatUnixDateTime = (value: number | string): string => {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) {
    return String(value)
  }

  const ms = numeric > 1e12 ? numeric : numeric * 1000
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const formatSigningType = (signingType?: string | null): string => {
  if (signingType === 'podpislon') {
    return 'Подпислон'
  }

  return '—'
}

export const getSignedStatusDetails = (
  contract: IContractSigningSource | null | undefined,
): string | null => {
  if (!contract || contract.signing_state !== 'signed') {
    return null
  }

  const details = contract.signing_details ?? {}
  const podpislon = contract.podpislon ?? {}

  const name =
    details.signer_name ??
    contract.signed_by_name ??
    podpislon.signer_name ??
    podpislon.contact_name
  const phone =
    details.signer_phone ??
    contract.signed_by_phone ??
    podpislon.signer_phone ??
    podpislon.phone
  const date =
    details.signed_at_label ??
    (details.signed_at != null ? formatUnixDateTime(details.signed_at) : null) ??
    contract.signed_at_label ??
    podpislon.signed_at_label ??
    (podpislon.signed_at != null
      ? formatUnixDateTime(podpislon.signed_at)
      : null)
  const code =
    details.sign_code ??
    contract.signing_code ??
    podpislon.signing_code ??
    podpislon.status_code

  const signer = [name, phone].filter(Boolean).join(', ')
  const parts = [signer || null, date || null, code ? `код: ${code}` : null].filter(
    Boolean,
  )

  return parts.length ? parts.join(' · ') : null
}

export const formatContractUploadedAt = (
  contract: IContractSigningSource | null | undefined,
): string => {
  if (!contract) return '—'

  const createdAt = contract.created_at ?? contract.uploaded_at
  if (createdAt === null || createdAt === undefined || createdAt === '') {
    if (contract.uploaded_at_label) {
      return String(contract.uploaded_at_label)
    }

    return '—'
  }

  if (typeof createdAt === 'number') {
    return formatUnixDateTime(createdAt)
  }

  const createdAtString = String(createdAt).trim()
  if (/^\d+$/.test(createdAtString)) {
    return formatUnixDateTime(createdAtString)
  }

  if (contract.uploaded_at_label) {
    return String(contract.uploaded_at_label)
  }

  return String(createdAt)
}

export const getContractFileName = (path?: string | null): string => {
  if (!path) return '—'
  const parts = String(path).split('/')
  return parts[parts.length - 1] || 'Файл'
}

export const isContractPdfFile = (path?: string | null): boolean => {
  if (!path) return false

  const fileName = getContractFileName(path)
  return /\.pdf$/i.test(fileName.trim())
}
